/**
 * Ops Error Client — zero-dep fetch wrapper for error reporting
 *
 * Modeled on shared/support-module/ops-center-client.ts.
 * Stores original window.fetch before any wrapping to prevent infinite loops.
 */

import type { ErrorConfig, BrowserErrorPayload } from './types';

// Store the original fetch BEFORE any wrapping happens
const originalFetch = typeof window !== 'undefined' ? window.fetch.bind(window) : fetch;

export class OpsErrorClient {
  private baseUrl: string;
  private apiKey: string;
  private platformId: string;

  constructor(config: ErrorConfig) {
    this.baseUrl = config.apiBaseUrl.replace(/\/$/, '');
    this.apiKey = config.apiKey;
    this.platformId = config.platformId;
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const res = await originalFetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': this.apiKey,
        ...options.headers,
      },
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`Error API ${res.status}: ${body}`);
    }
    if (res.status === 204) return undefined as T;
    return res.json();
  }

  async reportError(payload: BrowserErrorPayload): Promise<{ id: string; fingerprint: string; group_id: string }> {
    return this.request('/api/errors/ingest', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async reportBatch(payloads: BrowserErrorPayload[]): Promise<{ processed: number; total: number }> {
    return this.request('/api/errors/ingest/batch', {
      method: 'POST',
      body: JSON.stringify(payloads),
    });
  }

  /**
   * Use navigator.sendBeacon for page unload — more reliable than fetch.
   * Falls back to fetch if sendBeacon unavailable.
   */
  sendBeaconBatch(payloads: BrowserErrorPayload[]): boolean {
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) {
      // Fire-and-forget fetch fallback
      this.reportBatch(payloads).catch(() => {});
      return false;
    }

    const url = `${this.baseUrl}/api/errors/ingest/batch`;
    const data = JSON.stringify(payloads);

    // sendBeacon with Blob to set content-type
    const blob = new Blob([data], { type: 'text/plain' });
    return navigator.sendBeacon(url, blob);
  }

  get platform(): string {
    return this.platformId;
  }
}
